import * as z from 'zod';

export type PunchListAreaType = 'Sequence' | 'CA' | 'CC' | 'Text';

const getSequenceControlAreaSchema = (areaType: PunchListAreaType = 'Sequence') => {
    if (areaType === 'Text') {
        return z.string().optional()
            .refine((value) => !value || value.length <= 32, {
                message: 'Maximum 32 characters allowed',
            });
    }

    const maxDigits = areaType === 'Sequence' ? 4 : 3;
    const lengthMessage =
        areaType === 'Sequence'
            ? 'Sequences only support 4 numbers.'
            : 'Controls only supports 3 numbers.';

    return z.string().optional()
        .refine((value) => !value || /^\d+$/.test(value), {
            message: 'Numbers only, letters are not supported.',
        })
        .refine((value) => !value || value.length <= maxDigits, {
            message: lengthMessage,
        });
};

export const createDetailsSchema = (
    areaType: PunchListAreaType = 'Sequence',
    options?: { isLockedSequenceControlArea?: boolean },
) =>
    z.object({
        AssignedRootCauseText: z.string().optional(),
        AssignedRootCauseId: z.number().optional(),
        AssignedDisciplineId: z.number(),
        CreatorDisciplineId: z.number(),
        CreatorRootCauseId: z.number().optional(),
        CreatorRootCauseText: z.string().optional(),
        ProjectNumber: z.number(),
        Category: z.number(),
        Technology: z.number(),
        Issue: z.number(),
        DrawingHash: z.string().optional().refine((value) => !value || value.length <= 32, {
            message: "Maximum 32 characters allowed",
        }),
        ModelNumber: z.string().nullable().refine((value) => value !== null && value.trim().length > 0, {
            message: "Required",
        }).refine((value) => value === null || value.length <= 50, {
            message: "Maximum 50 characters allowed",
        }),
        // Checklist/task-fail values are prefilled and locked, so only enforce overall length.
        SequenceControlArea: options?.isLockedSequenceControlArea
            ? z.string().optional().refine((value) => !value || value.length <= 32, {
                message: 'Maximum 32 characters allowed',
            })
            : getSequenceControlAreaSchema(areaType),
        UnitDevice: z.string().optional().refine((value) => !value || value.length <= 64, {
            message: "Maximum 64 characters allowed",
        }),
        IssueDescription: z.string().nullable().refine((value) => value !== null && value.trim().length > 0, {
            message: "Required",
        }),
        DefectPic1: z.string().optional(),
        DefectPic2: z.string().optional(),
        DueDate: z.string().nonempty({ message: "Date Is Required" }),
        Priority: z.number(),
        DoesPunchListRequireReinspection: z.boolean(),
        ErmRelevant: z.boolean(),
        ErmType: z.string().optional(),
        punchListId: z.string().optional(),
        ChecklistUuid: z.string().optional(),
        CtUuid: z.string().optional(),
        InspectionTask: z.string().optional(),
    }).refine(data => {
        if (data.ErmRelevant) {
            return data.ErmType !== '';
        }
        return true;
    }, {
        message: "ERM type must be selected",
        path: ["ErmType"]
    });

export const detailsSchema = createDetailsSchema('Sequence');

export type PunchListDetailsType = z.infer<ReturnType<typeof createDetailsSchema>>;

export const siteMapSchema = z.object({
    DrawingUuid: z.string().optional(),
    Location: z.array(z.number()).optional(),
}).refine(data => {
    if (data.DrawingUuid) {
        return data.Location && data.Location.length === 2;
    }
    return true;
}, {
    message: "Location is required when a drawing is selected",
    path: ["Location"]
});

export type PunchListSiteMapType = z.infer<typeof siteMapSchema>;

export type PunchList = PunchListDetailsType & PunchListSiteMapType & {
    defectPic1?: string,
    storedDefectPic1?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    defectPic2?: string,
    storedDefectPic2?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    correctedPic1?: string,
    storedCorrectedPic1?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    correctedPic2?: string,
    storedCorrectedPic2?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    SequenceControlArea?: string,
    UnitDevice?: string,
    ChecklistUuid?: string,
    CtUuid?: string,
};

export type PunchlistApiType = {
    "punchListUuid": string,
    "projectNumber"?: string,
    "assignedRootCauseText": string | null,
    "assignedRootCauseId": number | null,
    "creatorDisciplineId": number | null,
    "creatorRootCauseId": number | null,
    "creatorRootCauseText": string | null,
    "assignedDisciplineId"?: number,
    "unitChecklistId": null,
    "checklistUuid": string | null,
    "ctUuid": string | null,
    "statusId": number,
    "categoryId": number | null,
    "issueId": number | null,
    "issueDesc": null,
    "priority": number,
    "dueDate": string,
    "rfrRequired": number,
    "drawingNumber": string | null,
    "isSiteProjectPunchList": string,
    "drawingId": number | null,
    "drawingUuid": string | null,
    "locationPin": string | null,
    resolverRootCauseId: number | null,
    finalInspectionRootCauseId: number | null,
    finalAgreedRootCauseId: number | null,
    defectPic1: string,
    "storedDefectPic1"?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    defectPic2: string,
    "storedDefectPic2"?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    correctedPic1: string,
    "storedCorrectedPic1"?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    correctedPic2: string,
    "storedCorrectedPic2"?: {
        value: string,
        notSync?: boolean,
        creation?: boolean,
    },
    "isDuplicate": string,
    "adhocSequenceCa": null,
    "adhocUnitDevice": string | null,
    "identifiedBy": string,
    "dateIdentified": string,
    "correctedBy": string,
    "dateCompleted": string,
    "isArchived": boolean,
    "ermRelevant": number | null,
    "ermType": string | null,
    notSync?: boolean,
    creation?: boolean,
    deletion?: boolean,
    // When true, the service worker skips the timestamp conflict check for this pending
    // offline update (status-only actions have no fresh modified timestamp to compare).
    syncIgnoreConflict?: boolean,
    technologyId: number | null,
    categoryV1Text: string | null
    issueV1Text: string | null
    onHoldReasonId?: number | null,
    onHoldDetail?: string | null,
    punchListTag?: string | null,
    modelNumber?: string | null,
    rollbackSnapshot?: Partial<PunchlistApiType>,
}

export const punchlistDefault: PunchlistApiType = {
    "punchListUuid": "",
    "projectNumber": "",
    "assignedDisciplineId": null,
    "assignedRootCauseText": "",
    "assignedRootCauseId": null,
    "creatorDisciplineId": null,
    "creatorRootCauseId": null,
    "creatorRootCauseText": "",
    "unitChecklistId": null,
    "checklistUuid": null,
    "ctUuid": null,
    "statusId": 0,
    "categoryId": null,
    "issueId": null,
    "issueDesc": null,
    "priority": 0,
    "dueDate": "",
    "rfrRequired": 0,
    "drawingNumber": null,
    "isSiteProjectPunchList": null,
    "drawingId": 0,
    "drawingUuid": null,
    "locationPin": "",
    "defectPic1": null,
    "defectPic2": null,
    "correctedPic1": null,
    "correctedPic2": null,
    "storedDefectPic1": null,
    "storedDefectPic2": null,
    "storedCorrectedPic1": null,
    "storedCorrectedPic2": null,
    "isDuplicate": null,
    "adhocSequenceCa": null,
    "adhocUnitDevice": null,
    "ermRelevant": null,
    "ermType": null,
    "identifiedBy": "",
    "dateIdentified": "",
    "correctedBy": null,
    "dateCompleted": null,
    "isArchived": false,
    resolverRootCauseId: null,
    finalInspectionRootCauseId: null,
    finalAgreedRootCauseId: null,
    technologyId: null,
    categoryV1Text: "",
    issueV1Text: "",
    punchListTag: null,
    modelNumber: null
}
